<script setup>
import { ref, computed, nextTick, onUnmounted } from 'vue'
import Sortable from 'sortablejs'
import ToolRailDropdown from './ToolRailDropdown.vue'

// Columns panel for a ToolRail, driven entirely by a `columns` controller so it
// works for any table (ip enrichments, generic selector types, Zeek log types).
// Controller shape:
//   { orderedFields:string[], totalCount, enabledCount, hasCustom,
//     labelFor(id), isEnabled(id), toggle(id), setOrder(ids), reset() }

const props = defineProps({
  // null / empty controller = nothing to configure; the dropdown hides itself.
  columns: {
    type: Object,
    default: null
  }
})

const columnListRef = ref(null)
const dropdownRef = ref(null)
let sortableInstance = null

const hasColumns = computed(() => !!props.columns && props.columns.totalCount > 0)

// Header names the thing whose view is being edited when the controller provides
// a `scopeLabel` (e.g. the displayed log type in Event Search).
const headerLabel = computed(() =>
  props.columns?.scopeLabel ? `Columns · ${props.columns.scopeLabel}` : 'Columns'
)

function initSortable() {
  nextTick(() => {
    if (columnListRef.value && !sortableInstance) {
      sortableInstance = Sortable.create(columnListRef.value, {
        animation: 150,
        handle: '.drag-handle',
        ghostClass: 'column-ghost',
        dragClass: 'column-drag',
        onEnd: (evt) => {
          const newOrder = [...props.columns.orderedFields]
          const [moved] = newOrder.splice(evt.oldIndex, 1)
          newOrder.splice(evt.newIndex, 0, moved)
          props.columns.setOrder(newOrder)
        }
      })
    }
  })
}

function destroySortable() {
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
}

// @close is the normal teardown, but it doesn't fire when the panel is open and
// the host unmounts (e.g. navigating away from Event Search) — that would strand
// the instance and its listeners on the teleported, now-detached list node.
onUnmounted(destroySortable)

function handleToggleField(field) {
  // Don't allow disabling the last enabled field.
  const c = props.columns
  if (c.isEnabled(field) && c.enabledCount <= 1) return
  c.toggle(field)
}

function handleReset() {
  props.columns.reset()
  if (dropdownRef.value) dropdownRef.value.close()
}
</script>

<template>
  <!-- Always rendered; disabled (rather than hidden) when there are no columns
       to configure, e.g. a non-table view mode or before a query runs. -->
  <ToolRailDropdown
    ref="dropdownRef"
    :icon="['fas', 'table-columns']"
    label="columns"
    :header-label="headerLabel"
    :header-subtext="hasColumns ? `${columns.enabledCount} of ${columns.totalCount} visible` : null"
    :disabled="!hasColumns"
    @open="initSortable"
    @close="destroySortable"
  >
    <template v-if="hasColumns">
      <div ref="columnListRef" class="column-list">
        <div
          v-for="field in columns.orderedFields"
          :key="field"
          class="column-item"
          :class="{ disabled: !columns.isEnabled(field) }"
        >
          <span class="drag-handle">⋮⋮</span>
          <label class="column-checkbox">
            <input
              type="checkbox"
              :checked="columns.isEnabled(field)"
              :disabled="columns.isEnabled(field) && columns.enabledCount <= 1"
              @change="handleToggleField(field)"
            />
            <span class="checkmark"></span>
          </label>
          <span class="column-label">{{ columns.labelFor(field) }}</span>
        </div>
      </div>

      <div v-if="columns.hasCustom" class="dropdown-divider"></div>

      <div v-if="columns.hasCustom" class="dropdown-item reset-item" @click="handleReset">
        <font-awesome-icon :icon="['fas', 'rotate-left']" class="item-icon" />
        <span>Reset to defaults</span>
      </div>
    </template>
  </ToolRailDropdown>
</template>

<style scoped>
/* Scoped to this component; the markup lives in ToolRailDropdown's slot but keeps
   this component's scope attribute, so the styles follow it after teleport. */
.column-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.column-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  transition: background 0.15s ease;
}

.column-item:hover {
  background: rgba(255, 255, 255, 0.03);
}

.column-item.disabled {
  opacity: 0.5;
}

.drag-handle {
  cursor: grab;
  color: var(--text-disabled);
  font-size: 0.75rem;
  padding: 0 0.25rem;
  user-select: none;
}

.drag-handle:hover {
  color: var(--text-muted);
}

.column-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.column-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  width: 16px;
  height: 16px;
  background: var(--border);
  border: 1px solid var(--surface-control);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.column-checkbox input:checked ~ .checkmark {
  background: var(--accent);
  border-color: var(--accent);
}

.column-checkbox input:checked ~ .checkmark::after {
  content: '';
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-bottom: 2px;
}

.column-checkbox input:disabled ~ .checkmark {
  opacity: 0.5;
  cursor: not-allowed;
}

.column-label {
  font-size: 0.85rem;
  color: var(--text-primary);
}

.column-ghost {
  opacity: 0.4;
  background: var(--accent-soft-2);
}

.column-drag {
  background: var(--surface-page) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.reset-item {
  color: var(--text-muted);
}

.reset-item:hover {
  color: var(--text-primary);
}

.column-list::-webkit-scrollbar {
  width: 6px;
}

.column-list::-webkit-scrollbar-track {
  background: transparent;
}

.column-list::-webkit-scrollbar-thumb {
  background: var(--surface-control);
  border-radius: var(--radius-xs);
}
</style>
